import type { ApprovalBox } from '@/pages/approval/paperForm';
import { budgetApprovalCells, type BudgetSignCell } from '@/pages/approval/overseasBudget';

/**
 * 하도급업체 계약 변경요청서 (V634 · V637 · 사용자 지시 2026-11-04 · 11-05 「우리 양식 말고 자체 양식 그대로」).
 *
 * 회사 엑셀 양식 그대로의 표 하나 — NO · 업체명 · 계약명 · 계약금액(VAT별도) · 계약기간(당초 · 변경) · 사유 · 비고.
 * 줄은 하도급관리의 계약 줄에서 온다({@link periodChangeDraftHref}). 원 계약 줄 id 를 들고 있어, 외주구매팀장 전결로
 * 끝나면 서버가 그 계약 묶음에 「변경」 차수 줄을 더한다(SubcontractPeriodChangeHandler).
 */

export const PERIOD_CHANGE_FORM_KEY = 'SUBCONTRACT_PERIOD_CHANGE';

/** 종이 제목 — 엑셀 1행. */
export const PERIOD_SHEET_TITLE = '하도급업체 계약 변경요청서';

/** 엑셀은 줄이 적어도 이만큼 그어져 있다 — 모자란 만큼 빈 줄로 채운다. */
export const PERIOD_SHEET_MIN_ROWS = 9;

/** 엑셀 열 너비 비율(%) — NO · 업체명 · 계약명 · 금액 · 당초 · 변경 · 사유 · 비고. */
export const PERIOD_SHEET_COLUMNS = [4.6, 12.1, 18.3, 9.9, 15.8, 15.8, 16.5, 7.0];

/**
 * 결재칸 — 엑셀 그대로 담당 · 대리 · 팀장 · 임원 · 대표이사 (사용자 결정 2026-11-05 「대리 칸에 소장」).
 * 담당은 기안자, 대리 칸에는 현장소장(기안자의 장)이 선다. 외주구매팀장 전결이라 대표이사 칸은 「전결」 이다.
 * 가르는 규칙은 해외 경비 예산의 결재칸({@link budgetApprovalCells})과 한 벌이다.
 */
export function periodApprovalCells(boxes: ApprovalBox[]): BudgetSignCell[] {
  const { site, hq } = budgetApprovalCells(boxes);
  return [hq[0]!, { ...site[1]!, label: '대 리' }, hq[1]!, hq[2]!, hq[3]!];
}

/** 「2026. 11. 05.」 → 「2026-11-05」 — 엑셀의 작성일 모양. */
export function periodSheetDate(dateLabel: string): string {
  const parts = dateLabel.match(/\d+/g);
  return parts && parts.length >= 3 ? `${parts[0]}-${parts[1]!.padStart(2, '0')}-${parts[2]!.padStart(2, '0')}` : dateLabel;
}

export type PeriodChangeRow = {
  /** 원 계약 줄 id — 결재 처리기가 이 줄의 묶음에 변경 차수를 더한다. */
  contractId: string;
  vendorName: string;
  contractName: string;
  /** VAT 별도 계약금액(원) — 숫자 글자. */
  amount: string;
  beforeStart: string;
  beforeEnd: string;
  afterStart: string;
  afterEnd: string;
  reason: string;
  note: string;
};

const text = (value: unknown): string => (value == null ? '' : String(value));

/** 모양을 맞춘다 — 서식에 없는 칸이 줄 안에 실려 가지 않게. 계약 id 가 없는 줄은 버린다. */
export function normalizePeriodRows(value: unknown): PeriodChangeRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => ({
      contractId: text(row.contractId),
      vendorName: text(row.vendorName),
      contractName: text(row.contractName),
      amount: text(row.amount).replace(/[^0-9]/g, ''),
      beforeStart: text(row.beforeStart),
      beforeEnd: text(row.beforeEnd),
      afterStart: text(row.afterStart),
      afterEnd: text(row.afterEnd),
      reason: text(row.reason),
      note: text(row.note),
    }))
    .filter((row) => row.contractId);
}

/** 「2024.11.16~2024.12.15」 — 양식의 기간 칸 모양. 비면 「-」. */
export function periodText(start: string, end: string): string {
  const dot = (date: string) => date.replace(/-/g, '.');
  if (!start && !end) return '-';
  return `${dot(start)}~${dot(end)}`;
}

/** 하도급관리의 계약 줄 하나 — 기안을 미리 채울 때 필요한 값만. */
export type PeriodChangeSource = {
  id: string;
  siteId: string;
  siteName: string;
  vendorName: string | null;
  trade: string;
  workName: string | null;
  amountWithoutVat: number | null;
  startDate: string | null;
  endDate: string | null;
};

/** 계약 하나로 표의 한 줄 — 업체 · 계약명 · 금액 · 당초 기간은 계약에서, 변경 기간은 시작일만 당초 그대로. */
export function periodRowFrom(contract: PeriodChangeSource): PeriodChangeRow {
  return {
    contractId: contract.id,
    vendorName: contract.vendorName ?? '',
    contractName: contract.workName || contract.trade,
    amount: contract.amountWithoutVat == null ? '' : String(Math.round(contract.amountWithoutVat)),
    beforeStart: contract.startDate ?? '',
    beforeEnd: contract.endDate ?? '',
    afterStart: contract.startDate ?? '',
    afterEnd: '',
    reason: '',
    note: '',
  };
}

/** 기안 표의 업체 고르개 한 줄 — 그 현장 하도급관리의 계약 묶음 하나(지금 유효한 차수). */
export type PeriodContractOption = { label: string; source: PeriodChangeSource };

/**
 * 하도급관리 시트의 계약들 → 고르개. 같은 No. 묶음은 <b>마지막 차수</b> 한 줄만 — 변경이 쌓인 계약은 그 기간이
 * 지금 기간이다. 검토중(DRAFT)은 아직 계약이 아니라 뺀다. 차례는 시트 그대로.
 */
export function periodContractOptions(
  site: { id: string; name: string },
  contracts: Array<{
    id: string; lineNo: number; revisionNo: number; lineLabel: string; status: string; trade: string;
    workName: string | null; vendorName: string | null; amountWithoutVat: number | null;
    startDate: string | null; endDate: string | null;
  }>,
): PeriodContractOption[] {
  const latest = new Map<number, (typeof contracts)[number]>();
  for (const row of contracts) {
    const seen = latest.get(row.lineNo);
    if (!seen || row.revisionNo > seen.revisionNo) latest.set(row.lineNo, row);
  }
  return [...latest.values()]
    .filter((row) => row.status !== 'DRAFT')
    .map((row) => ({
      label: [row.lineLabel, row.vendorName, row.workName || row.trade].filter(Boolean).join(' · '),
      source: { ...row, siteId: site.id, siteName: site.name },
    }));
}

/**
 * 그 계약을 채운 기안 화면 주소 — 계약 상세의 [계약기간 변경 요청] 과 계약 종료 알림 할 일이 같은 주소를 연다.
 * 변경 기간은 비워 둔다 — 시작일은 당초 그대로 두고 종료일만 적으면 된다.
 */
export function periodChangeDraftHref(contract: PeriodChangeSource): string {
  const name = contract.workName || contract.trade;
  const row = periodRowFrom(contract);
  const data = { siteName: contract.siteName, siteId: contract.siteId, periodRows: [row] };
  const title = `[계약기간 변경] ${contract.siteName} ${contract.vendorName ?? ''} ${name}`.replace(/\s+/g, ' ').trim();
  return `/approval/draft?formKey=${PERIOD_CHANGE_FORM_KEY}&title=${encodeURIComponent(title)}&data=${encodeURIComponent(JSON.stringify(data))}`;
}
